import React, { useMemo } from 'react';
import { curveMonotoneX } from '@visx/curve';
import { ComposedChart } from '../../charts/composed-chart';
import { SeriesBar } from '../../charts/series-bar';
import { Line } from '../../charts/line';
import { Grid } from '../../charts/grid';
import { XAxis } from '../../charts/x-axis';
import { YAxis } from '../../charts/y-axis';
import { ChartTooltip } from '../../charts/tooltip';
import { getFormatter } from '../formatters';
import {
  useIsDarkMode,
  useChartPaletteId,
  compactFormat,
  seriesColor,
  SYNTH_X_KEY,
  toBklitRows,
  seriesHasData,
  ChartSkeletonLayer,
  isNumeric,
  labelColor,
  RnPointLabels,
} from './chartTheme';
import { isInkShell } from '../../../styles/chartColors';
import { ChartLegend } from './ChartLegend';
import { prefersReducedMotion } from '../AnimatedValue';
import '../ReportsNative.css';

/**
 * Combo column + line overlay (per operations-labor.html "Hours Worked
 * vs Budget": orange columns with in-bar labels, onyx budget line).
 * Rendered with the vendored bklit ComposedChart (SeriesBar + Line) —
 * categorical x values get synthetic dates per the LODGEIC.md recipe.
 *
 * Props:
 *   data, xKey
 *   series    [{ key, label, color?, type: 'bar'|'line' }] — order matters;
 *             bars draw first, lines overlay
 *   labels    'bar' (white in-bar labels) | 'top' | false (default false)
 *   yFormat   (default 'numberCompact'), currency
 *   height    (default 240), legend (default true), barSize, yDomain
 *             (yDomain kept for API compatibility — kit derives the domain)
 */
export function ColumnTrendChart({
  data = [],
  xKey,
  series = [],
  labels = false,
  yFormat = 'numberCompact',
  currency,
  height = 240,
  legend = true,
  // 'peak' (default) labels only the top of the scale — the high-water
  // mark. 'full' restores the tick ladder for charts read for values
  // rather than shape.
  yAxis = 'peak',
  barSize,
  yDomain, // eslint-disable-line no-unused-vars -- kit derives domain from data
  margin,
  loading = false,
}) {
  const dark = useIsDarkMode();
  useChartPaletteId(); // repaint on a palette switch (series colours read the palette live)
  const fmt = getFormatter(yFormat, currency ? { currency } : undefined);
  const tickFmt = getFormatter(compactFormat(yFormat), currency ? { currency } : undefined);
  const labelMode = labels === true ? 'bar' : labels;

  const rows = useMemo(
    () =>
      toBklitRows(data, series.map((s) => s.key), {
        // Bars keep nulls (kit skips them); only line overlays get gap fill.
        fillKeys: series.filter((s, i) => (s.type || (i === 0 ? 'bar' : 'line')) === 'line').map((s) => s.key),
      }),
    [data, series]
  );
  const xLabels = useMemo(() => rows.map((r) => String(r.__rnSrc?.[xKey] ?? '')), [rows, xKey]);

  // See TrendAreaChart: the chart stays mounted and only the skeleton layer
  // swaps out when the payload lands.
  const skeleton = loading && rows.length === 0;

  const base = margin || { top: 12, right: 16, bottom: 0, left: 0 };
  const kitMargin = {
    top: base.top,
    right: base.right,
    bottom: base.bottom + (isInkShell() ? 24 : 26),
    left: base.left + (isInkShell() ? 46 : 52),
  };

  const seriesType = (s, i) => s.type || (i === 0 ? 'bar' : 'line');
  const barSeries = series.filter((s, i) => seriesType(s, i) === 'bar');

  return (
    <div>
      {legend && <ChartLegend series={series} />}
      <div className="rn-chart-body" style={{ height }}>
        <ComposedChart
          // Loading is a LAYER here, not the kit's `status` choreography —
          // see ChartSkeletonLayer for why.
          status="ready"
          className="rn-bklit"
          data={rows}
          xDataKey={SYNTH_X_KEY}
          xLabels={xLabels}
          margin={kitMargin}
          aspectRatio={null}
          style={{ height: '100%' }}
          maxBarSize={barSize || 30}
          animationDuration={prefersReducedMotion() ? 0 : undefined}
        >
          <Grid horizontal numTicksRows={4} />
          <XAxis />
          {!skeleton && <YAxis peak={yAxis === 'peak'} numTicks={4} formatValue={(v) => tickFmt(v)} />}
          {!skeleton && (
            <ChartTooltip
              title={null}
              rows={(point) =>
                series.map((s, i) => ({
                  color: seriesColor(s, i, dark),
                  label: s.label || s.key,
                  value: fmt(point.__rnSrc?.[s.key]),
                }))
              }
            />
          )}
          {skeleton && <ChartSkeletonLayer kind="bar" color={seriesColor(series[0] || {}, 0, dark)} />}
          {series.map((s, i) => {
            if (!seriesHasData(rows, s.key)) return null;
            const color = seriesColor(s, i, dark);
            if (seriesType(s, i) === 'bar') {
              return <SeriesBar key={s.key} dataKey={s.key} fill={color} radius={3} />;
            }
            return (
              <Line
                key={s.key}
                dataKey={s.key}
                curve={curveMonotoneX}
                stroke={color}
                strokeWidth={2}
                showMarkers={false}
              />
            );
          })}
          {labelMode &&
            !skeleton &&
            barSeries.map((s) => (
              <RnPointLabels
                key={`labels-${s.key}`}
                k={s.key}
                formatter={(v) => (isNumeric(v) ? fmt(v) : '')}
                fill={labelMode === 'bar' ? '#FFFFFF' : labelColor(dark)}
                fontSize={labelMode === 'bar' ? 9 : 9.5}
                position={labelMode === 'bar' ? 'inside' : 'top'}
              />
            ))}
        </ComposedChart>
      </div>
    </div>
  );
}

export default ColumnTrendChart;
